import { skipToken } from "@reduxjs/toolkit/query";
import type {
  PlaybookId,
  PlaybookSelection,
} from "@repo/app-types/entities/playbook";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { useMemo } from "react";
import { useAllMinimalPlaybooksQuery } from "../api/entities/playbook.js";
import { useAllTeammateAgentsQuery } from "../api/entities/teammateAgent.js";

/**
 * One row of the shared playbook/voice-agent option list. Voice agents are
 * valued by the playbook backing the agent; `kind` is what separates them, so
 * filters and transforms apply to the whole list and can't miss one kind.
 */
export interface PlaybookAndVoiceAgentOption extends SimpleSelectOption<PlaybookId> {
  kind: "playbook" | "voice_agent";
}

export interface PlaybookAndVoiceAgentOptions {
  /** Coaching playbooks followed by voice agents. */
  options: readonly PlaybookAndVoiceAgentOption[];
  isLoading: boolean;
  /**
   * True once every backing query has succeeded, so `options` is the complete
   * list. False while loading and after a failed fetch — `isLoading` alone
   * can't tell an error's empty `options` from a genuinely empty company.
   */
  hasLoadedAllOptions: boolean;
}

export interface PlaybookAndVoiceAgentSection {
  id: string;
  /** Omitted when the menu has a single, unlabeled section. */
  label?: string;
  options: readonly PlaybookAndVoiceAgentOption[];
}

/**
 * Options for pickers that choose which calls to analyze: coaching playbooks
 * plus voice agents, as one `kind`-tagged list. A voice agent's calls are
 * graded against the playbook backing the agent, so voice agent options carry
 * that playbook's id and no caller needs to treat the two kinds differently.
 */
export function usePlaybookAndVoiceAgentOptions(hookOptions?: {
  shouldSkipVoiceAgents?: boolean;
}): PlaybookAndVoiceAgentOptions {
  const shouldSkipVoiceAgents = hookOptions?.shouldSkipVoiceAgents === true;
  const {
    data: playbooks,
    isLoading: arePlaybooksLoading,
    isSuccess: havePlaybooksLoaded,
  } = useAllMinimalPlaybooksQuery({
    shouldExcludeTeammateAgentPlaybooks: true,
  });
  const {
    data: teammateAgents,
    isLoading: areTeammateAgentsLoading,
    isSuccess: haveTeammateAgentsLoaded,
  } = useAllTeammateAgentsQuery(shouldSkipVoiceAgents ? skipToken : undefined);

  const options = useMemo<readonly PlaybookAndVoiceAgentOption[]>(
    () => [
      ...(playbooks?.map(
        (playbook): PlaybookAndVoiceAgentOption => ({
          value: playbook.id,
          label: playbook.name,
          kind: "playbook",
        }),
      ) ?? []),
      ...(teammateAgents?.map(
        (agent): PlaybookAndVoiceAgentOption => ({
          value: agent.teammate_agent_config.playbook_id,
          label: agent.name,
          kind: "voice_agent",
        }),
      ) ?? []),
    ],
    [playbooks, teammateAgents],
  );

  return {
    options,
    isLoading: arePlaybooksLoading || areTeammateAgentsLoading,
    hasLoadedAllOptions:
      havePlaybooksLoaded &&
      (shouldSkipVoiceAgents || haveTeammateAgentsLoaded),
  };
}

export function checkIsVoiceAgentOption(
  option: PlaybookAndVoiceAgentOption,
): boolean {
  return option.kind === "voice_agent";
}

/**
 * Groups the options into "Playbook" / "Voice agent" sections. With no voice
 * agents the single playbook section is unlabeled, so the menu reads as a
 * plain playbook list.
 */
export function buildPlaybookAndVoiceAgentSections(
  options: readonly PlaybookAndVoiceAgentOption[],
): readonly PlaybookAndVoiceAgentSection[] {
  const playbookOptions = options.filter(
    (option) => !checkIsVoiceAgentOption(option),
  );
  const voiceAgentOptions = options.filter(checkIsVoiceAgentOption);

  if (voiceAgentOptions.length === 0) {
    return [{ id: "playbooks", options: playbookOptions }];
  }

  return [
    { id: "playbooks", label: "Playbook", options: playbookOptions },
    { id: "voice-agents", label: "Voice agent", options: voiceAgentOptions },
  ];
}

/**
 * The dynamic (non-id-pinned) selection options, keyed by select-option id.
 * Each entry pairs its display label with the `PlaybookSelection` it stands
 * for. Shared by the signal scope pickers and the agents-page playbook filter
 * so every surface offers the same list.
 */
export const DYNAMIC_SELECTION_OPTIONS = {
  all: { label: "All playbooks and voice agents", selection: { type: "all" } },
  all_playbooks: {
    label: "All playbooks",
    selection: { type: "all_playbooks" },
  },
  all_voice_agents: {
    label: "All voice agents",
    selection: { type: "all_voice_agents" },
  },
} as const satisfies Record<
  string,
  { label: string; selection: PlaybookSelection }
>;

export type DynamicSelectionOptionId = keyof typeof DYNAMIC_SELECTION_OPTIONS;

export function isDynamicSelectionOptionId(
  id: string,
): id is DynamicSelectionOptionId {
  return id in DYNAMIC_SELECTION_OPTIONS;
}

/**
 * The dynamic option rows offered above the id-pinned sections. Without voice
 * agents the dynamic split is noise — only "all" is offered, labeled as plain
 * "All playbooks".
 */
export function buildDynamicSelectionOptions(
  hasVoiceAgents: boolean,
): readonly { id: DynamicSelectionOptionId; label: string }[] {
  return hasVoiceAgents
    ? Object.entries(DYNAMIC_SELECTION_OPTIONS).map(([id, { label }]) => ({
        id: id as DynamicSelectionOptionId,
        label,
      }))
    : [{ id: "all", label: DYNAMIC_SELECTION_OPTIONS.all_playbooks.label }];
}
